import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useContext,
  useRef,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { useFetchApi } from "../../../hooks/useFetchApi";
import { DispatchContext } from "../../../store/context";
import { UPDATE_BASE_CURRENCY } from "../../../store/types";
import {
  getExchangeInfo,
  marketCurrencyMap,
  marketLogoMap,
  STOCK_EXCHANGES,
} from "../../../helper";

// ACL Cables PLC and its sector peer Kelani Cables PLC.
const DEFAULT_STOCK_IDS = [5778, 6009];
const STORAGE_KEY = "dynamic_graphs_selected_tickers";

interface StockSearchModalProps {
  loading?: boolean;
  selectedStocks: any[];
  setSelectedSotcks: (value: any[]) => void;
}

export const StockSelectOptions: React.FC<StockSearchModalProps> = ({
  loading = false,
  selectedStocks,
  setSelectedSotcks,
}) => {
  const [stocks, setStocks] = useState<any[]>([]);
  const [filteredStocks, setFilteredStocks] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedExchange, setSelectedExchange] = useState<string>("all");
  const dropdownRef = useRef<HTMLDivElement>(null);
  // The dropdown panel itself is portaled to document.body (see the
  // isOpen block in the return below) — .stock-section, its normal DOM
  // parent, carries `.vi-glass-card`'s `backdrop-filter`, which
  // establishes a CSS stacking context. That trapped the panel's
  // z-index inside that context no matter how high the value, so later
  // page content (the Period selector, ChartTabs' own glass card) still
  // painted over it. A portal sidesteps that entirely: the panel
  // becomes a sibling of the whole app root instead of a descendant of
  // the card that was capping it.
  const panelRef = useRef<HTMLDivElement>(null);
  // No `width` here anymore — the trigger is now a compact "+ Add stock"
  // button rather than a full-width input, and the panel keeps its own
  // fixed width (see the `w-[300px]` on the panel below) instead of
  // matching whatever narrow width that button happens to be.
  const [panelRect, setPanelRect] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const hasRestored = useRef(false);

  const {
    response,
    loading: stockLoading,
    execute: getStocks,
  } = useFetchApi("/api/all-stocks", {}, "GET");

  // Click outside handler — the dropdown panel is portaled to
  // document.body, so it's no longer a DOM descendant of dropdownRef;
  // a click inside the (portaled) panel must also count as "inside".
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedTrigger = dropdownRef.current?.contains(target);
      const clickedPanel = panelRef.current?.contains(target);
      if (!clickedTrigger && !clickedPanel) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Track the trigger field's screen position while the panel is open,
  // so the portaled panel can be pinned directly under it.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelRect(null);
      return;
    }

    const updateRect = () => {
      const rect = dropdownRef.current?.getBoundingClientRect();
      if (rect) {
        setPanelRect({ top: rect.bottom + 4, left: rect.left });
      }
    };

    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [isOpen]);

  // Fetch stocks
  useEffect(() => {
    getStocks();
  }, []);

  // Process stocks
  useEffect(() => {
    if (response && Object.keys(response).length) {
      const stocksFromExchanges = STOCK_EXCHANGES?.flatMap(
        (exchange) =>
          (response &&
            response?.find((section: any) => section?.section === exchange)
              ?.options) ||
          [],
      );

      const activeStocks = stocksFromExchanges.filter(
        (stock) => stock.status === 1,
      );

      setStocks(activeStocks);
      setFilteredStocks(stocksFromExchanges);
    }
  }, [response]);

  // Restore previous selection, or fall back to default, once stocks load
  useEffect(() => {
    if (stocks.length > 0 && !hasRestored.current) {
      hasRestored.current = true;

      if (selectedStocks.length > 0) return; // already has a selection, don't override

      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        const parsed = saved ? JSON.parse(saved) : null;

        if (parsed?.cleared) {
          // User explicitly cleared last time — respect it
          return;
        }

        const savedIds: number[] = parsed?.ids || [];
        const savedStocks = stocks.filter((s) => savedIds.includes(s.id));

        if (savedStocks.length > 0) {
          setSelectedSotcks(savedStocks);
          return;
        }
      } catch {
        // ignore parse errors
      }

      const defaultStocks = DEFAULT_STOCK_IDS.map((id) =>
        stocks.find((s) => s.id === id),
      ).filter(Boolean);
      if (defaultStocks.length > 0) {
        setSelectedSotcks(defaultStocks);
      }
    }
  }, [stocks]);

  // Persist the selection whenever it changes (including removals from parent)
  useEffect(() => {
    if (!hasRestored.current) return; // don't persist before restore has run

    if (selectedStocks.length > 0) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ids: selectedStocks.map((s) => s.id),
        }),
      );
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ cleared: true }));
    }
  }, [selectedStocks]);

  // Get unique stock exchanges from stocks
  const stockExchanges = useMemo(() => {
    const exchanges = new Set<string>();
    stocks?.forEach((stock) => {
      if (stock.market_name) {
        exchanges.add(stock.market_name);
      }
    });
    return Array.from(exchanges).sort();
  }, [stocks]);

  useEffect(() => {
    if (!stocks || stocks.length === 0) return;

    let filtered = stocks.filter((stock) => {
      // Check if stock is already selected
      const isAlreadySelected = selectedStocks.some(
        (selectedStock) => selectedStock.id === stock.id,
      );

      // If stock is already selected, don't include it
      if (isAlreadySelected) return false;

      // Filter by exchange
      if (
        selectedExchange !== "all" &&
        stock.market_name !== selectedExchange
      ) {
        return false;
      }

      // If no search term, return all non-selected stocks
      if (!searchTerm) return true;

      // Convert search term to lowercase for case-insensitive search
      const normalizedSearchTerm = searchTerm.toLowerCase();

      // Check if search term matches ticker symbol or company name
      return (
        stock.ticker_symbol?.toLowerCase().includes(normalizedSearchTerm) ||
        stock.company_name?.toLowerCase().includes(normalizedSearchTerm)
      );
    });

    // Update filtered stocks state
    setFilteredStocks(filtered);
  }, [searchTerm, stocks, selectedStocks, selectedExchange]);

  const dispatch = useContext(DispatchContext);

  const handleStockSelect = (stock: any) => {
    if (selectedStocks.length < 5) {
      const currency = marketCurrencyMap[stock?.short_name] ?? "USD";

      setSelectedSotcks([...selectedStocks, stock]);
      dispatch({ type: UPDATE_BASE_CURRENCY, payload: currency });

      setSearchTerm("");
      setIsOpen(false);
    }
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Trigger — a search box, on its own row below the selected-stock
          chips (parent renders it that way now) rather than inline
          alongside them, so it keeps a fixed, full-width bar instead of
          shrinking or drifting around however many chips are selected. */}
      <div
        className={`vi-dg-control w-full !h-9 flex items-center px-3 ${
          isOpen ? "!border-[color:var(--vi-accent)]" : ""
        } ${selectedStocks.length === 5 ? "opacity-50 !cursor-not-allowed" : ""}`}
        onClick={() => selectedStocks.length < 5 && setIsOpen(true)}
      >
        <input
          type="text"
          placeholder="Search stocks"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          disabled={selectedStocks.length === 5}
          className={`w-full !outline-none custom_selcet !border-none h-7 !bg-transparent text-sm text-[color:var(--vi-text)]
            placeholder:text-[color:var(--vi-text-tertiary)] focus:!outline-none focus:!border-none ${
              selectedStocks.length === 5
                ? "opacity-50 !cursor-not-allowed"
                : ""
            }`}
        />
      </div>

      {isOpen &&
        panelRect &&
        createPortal(
          <div
            ref={panelRef}
            className="vi-glass-card vi-dg-stock-popup fixed z-[9999] w-[300px] max-h-96 overflow-y-auto"
            style={{
              top: panelRect.top,
              left: panelRect.left,
              fontFamily: "Inter, sans-serif",
            }}
          >
            {/* Exchange Filter Buttons */}
          <div className="vi-dg-exchange-bar p-3 sticky top-0 z-10">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedExchange("all");
                }}
                className={`vi-dg-pill px-3 py-1.5 text-xs font-medium ${
                  selectedExchange === "all" ? "vi-dg-pill-active" : ""
                }`}
              >
                All Exchanges
              </button>
              {stockExchanges.map((exchange) => {
                const { logo, short } = getExchangeInfo(exchange);
                return (
                  <button
                    key={exchange}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedExchange(exchange);
                    }}
                    className={`vi-dg-pill px-3 py-1.5 text-xs font-medium flex items-center gap-2 ${
                      selectedExchange === exchange ? "vi-dg-pill-active" : ""
                    }`}
                  >
                    {logo && (
                      <img
                        src={logo}
                        alt={short}
                        className="w-4 h-4 object-cover"
                      />
                    )}
                    {short}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stock List */}
          <div className="max-h-60 overflow-y-auto">
            {stockLoading ? (
              <div className="p-3 text-center text-[color:var(--vi-text-tertiary)]">Loading...</div>
            ) : filteredStocks.length === 0 ? (
              <div className="p-3 text-center text-[color:var(--vi-text-tertiary)]">
                No stocks found
              </div>
            ) : (
              filteredStocks.map((stock) => (
                <div
                  // Stable key — no Math.random() fallback: that made
                  // React tear down and recreate this node on every
                  // re-render (every keystroke, every exchange-filter
                  // click) whenever a stock lacked `.id`, which is what
                  // made switching exchanges feel janky/not smooth.
                  key={`${stock.market_name}-${stock.ticker_symbol}`}
                  className="
                    px-3 py-2
                    hover:bg-[color:var(--vi-surface-hover)]
                    cursor-pointer
                    flex justify-between items-center
                  "
                  onClick={() => handleStockSelect(stock)}
                >
                  <div className="flex flex-col w-full">
                    <div className=" w-full flex items-center justify-between ">
                      <div className="flex items-center gap-x-2">
                        <img
                          src={`/${stock.company_logo}`}
                          alt={stock.company_logo}
                          className=" w-6 h-6 object-contain"
                        />
                        <span className="font-dm text-sm text-[color:var(--vi-text)] ">
                          {stock.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-x-2">
                        <img
                          src={marketLogoMap[stock?.market_name] ?? ""}
                          alt=""
                          className="w-6 h-6 object-cover"
                        />
                        <span className=" text-[color:var(--vi-text-secondary)] font-dm text-sm ">
                          {stock.short_name}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
